import { CalendarDaysIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
  endOfDay,
  endOfMonth,
  startOfDay,
  startOfMonth,
  subDays,
} from "date-fns";
import { fr } from "date-fns/locale";
import { useEffect, useMemo, useRef, useState } from "react";
import { createStaticRanges, DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { ACCENT } from "../../pages/Places/PlacesTable";

interface DateFiltersProps {
  label?: string;
  onChange?: (dates: { from: string; to: string }) => void;
  defaultStartDate?: string;
  defaultEndDate?: string;
}

interface Range {
  startDate?: Date;
  endDate?: Date;
  key: "selection";
}

const toRange = (start?: string, end?: string): Range => ({
  startDate: start ? new Date(start) : undefined,
  endDate: end ? new Date(end) : undefined,
  key: "selection",
});

const formatDate = (date?: Date) =>
  date
    ? date.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "—";

const STATIC_RANGES = createStaticRanges([
  {
    label: "Aujourd'hui",
    range: () => ({
      startDate: startOfDay(new Date()),
      endDate: endOfDay(new Date()),
    }),
  },
  {
    label: "Hier",
    range: () => ({
      startDate: startOfDay(subDays(new Date(), 1)),
      endDate: endOfDay(subDays(new Date(), 1)),
    }),
  },
  {
    label: "7 derniers jours",
    range: () => ({
      startDate: startOfDay(subDays(new Date(), 6)),
      endDate: endOfDay(new Date()),
    }),
  },
  {
    label: "Ce mois-ci",
    range: () => ({
      startDate: startOfMonth(new Date()),
      endDate: endOfMonth(new Date()),
    }),
  },
]);

export default function CalendarDateFilters({
  label,
  onChange,
  defaultStartDate,
  defaultEndDate,
}: DateFiltersProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // `applied` is what the filter uses, `draft` is what the picker is editing
  const [applied, setApplied] = useState<Range>(() =>
    toRange(defaultStartDate, defaultEndDate),
  );
  const [draft, setDraft] = useState<Range>(applied);

  // Stay in sync when the parent changes the dates (URL reload, reset...)
  useEffect(() => {
    const next = toRange(defaultStartDate, defaultEndDate);
    setApplied(next);
    setDraft(next);
  }, [defaultStartDate, defaultEndDate]);

  // Close on outside click
  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setDraft(applied);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [applied]);

  const hasApplied = Boolean(applied.startDate && applied.endDate);
  const canApply = Boolean(draft.startDate && draft.endDate);

  const apply = () => {
    if (!draft.startDate || !draft.endDate) return;
    const to = new Date(draft.endDate);
    to.setHours(23, 59, 59, 999);
    setApplied(draft);
    onChange?.({ from: draft.startDate.toISOString(), to: to.toISOString() });
    setOpen(false);
  };

  const reset = () => {
    const empty = toRange();
    setApplied(empty);
    setDraft(empty);
    onChange?.({ from: "", to: "" });
    setOpen(false);
  };

  const buttonText = useMemo(
    () =>
      hasApplied
        ? `${formatDate(applied.startDate)} → ${formatDate(applied.endDate)}`
        : "Sélectionner une période",
    [hasApplied, applied],
  );

  return (
    <div
      ref={wrapperRef}
      className="d-flex flex-column gap-1"
      style={{ position: "relative" }}
    >
      {label && (
        <span className="text-muted" style={{ fontSize: 11 }}>
          {label}
        </span>
      )}

      <div
        className="d-flex align-items-center"
        style={{
          height: 38,
          borderRadius: 999,
          border: `1px solid ${hasApplied ? ACCENT : "#e5e5e5"}`,
          backgroundColor: "#fff",
        }}
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="border-0 bg-transparent d-flex align-items-center gap-2 ps-3 h-100"
          style={{
            fontSize: 13,
            color: hasApplied ? "#222" : "#777",
            paddingRight: hasApplied ? 6 : 16,
            whiteSpace: "nowrap",
          }}
        >
          <CalendarDaysIcon
            width={18}
            height={18}
            color={hasApplied ? ACCENT : "gray"}
          />
          {buttonText}
        </button>

        {hasApplied && (
          <button
            type="button"
            onClick={reset}
            aria-label="Effacer la période"
            className="border-0 bg-transparent d-flex align-items-center pe-3 h-100"
          >
            <XMarkIcon width={16} height={16} color="gray" />
          </button>
        )}
      </div>

      {open && (
        <div
          style={{
            position: "absolute",
            zIndex: 100,
            top: "calc(100% + 6px)",
            left: 0,
            maxWidth: "calc(100vw - 32px)",
            overflowX: "auto",
            background: "#fff",
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            borderRadius: 16,
            padding: 12,
          }}
        >
          <DateRangePicker
            ranges={[draft]}
            onChange={(ranges: any) => setDraft(ranges.selection)}
            moveRangeOnFirstSelection={false}
            rangeColors={[ACCENT]}
            locale={fr}
            staticRanges={STATIC_RANGES}
            inputRanges={[]}
            months={1}
            direction="horizontal"
          />

          <div className="d-flex justify-content-end gap-2 pt-2">
            <button
              type="button"
              onClick={reset}
              className="btn btn-sm btn-outline-secondary rounded-pill px-3"
            >
              Réinitialiser
            </button>
            <button
              type="button"
              onClick={apply}
              disabled={!canApply}
              className="btn btn-sm btn-primary text-white rounded-pill px-3"
            >
              Appliquer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
